<template>
  <div class="relative flex justify-center items-center gap-1 rounded-lg text-slate-100">
    <button
      v-for="item in primaryTools"
      :key="item.id"
      type="button"
      class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-[var(--p-surface-500)] dark:text-[var(--p-primary-50)] transition-colors duration-150 hover:bg-[var(--p-primary-color)]/10 dark:hover:bg-[var(--p-primary-900)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--p-primary-color)]/50 focus-visible:ring-[var(--p-primary-color)]/50"
      :class="{
        '!bg-[var(--p-primary-color)]/90 dark:!bg-[var(--p-primary-700)]/80 !text-white ':
          Boolean(item.activeTool && activeTool === item.activeTool) || activePanel === item.id,
      }"
      :title="item.label"
      @click="selectPrimaryTool(item.id, $event)"
    >
      <component :is="item.icon" :size="18" />
    </button>

    <EmojiPicker ref="emojiPickerRef" hide-trigger position="bottom" @emoji="selectEmoji" />

    <Transition name="toolbar-panel">
      <div
        v-if="activePanel"
        class="absolute top-12 z-20 flex max-w-[min(92vw,760px)] flex-wrap items-center gap-2 rounded-lg border dark:border-[var(--p-surface-600)] bg-white dark:bg-[var(--p-surface-800)] p-2 shadow-xl/10"
      >
        <template v-if="activePanel === 'crop'">
          <AppSelect
            :model-value="cropRatio"
            :options="cropOptions"
            :filter="false"
            :show-clear="false"
            option-label="name"
            option-value="value"
            container-class=" "
            input-class="!h-9 !rounded-md !text-xs w-25"
            @update:model-value="emit('crop-ratio', String($event || 'free'))"
          />
          <button
            type="button"
            class="flex h-9 w-9 items-center justify-center rounded-md text-[var(--p-surface-500)] transition-colors duration-150 hover:bg-[var(--p-primary-color)]/10 dark:text-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-600)]/60"
            title="Deshacer recorte"
            @click="emit('crop-undo')"
          >
            <Undo2 :size="16" />
          </button>
          <button
            type="button"
            class="flex h-9 w-9 items-center justify-center rounded-md text-[var(--p-surface-500)] transition-colors duration-150 hover:bg-[var(--p-primary-color)]/10 dark:text-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-600)]/60"
            title="Rotar a la izquierda"
            @click="emit('rotate-left')"
          >
            <RotateCcw :size="16" />
          </button>
          <button
            type="button"
            class="flex h-9 w-9 items-center justify-center rounded-md text-[var(--p-surface-500)] transition-colors duration-150 hover:bg-[var(--p-primary-color)]/10 dark:text-[var(--p-surface-100)] dark:hover:bg-[var(--p-surface-600)]/60"
            title="Rotar a la derecha"
            @click="emit('rotate-right')"
          >
            <RotateCw :size="16" />
          </button>
        </template>

        <template v-else-if="activePanel === 'filters'">
          <button
            v-for="filter in filters"
            :key="filter.value"
            type="button"
            class="h-9 rounded-md px-3 text-xs font-medium transition-colors duration-150 hover:bg-[var(--p-primary-color)]/10 dark:hover:bg-[var(--p-surface-600)]/60"
            :class="
              selectedFilter === filter.value
                ? '!bg-[var(--p-primary-color)]/90 dark:!bg-[var(--p-surface-600)] text-white'
                : 'text-[var(--p-surface-500)] dark:text-[var(--p-surface-200)]'
            "
            @click="selectFilter(filter.value)"
          >
            {{ filter.label }}
          </button>
        </template>

        <template v-else-if="activePanel === 'draw'">
          <div class="flex h-9 items-center gap-1.5 px-1">
            <button
              v-for="presetColor in presetColors"
              :key="presetColor"
              type="button"
              class="h-6 w-6 rounded-full border transition hover:scale-110"
              :class="
                color.toLowerCase() === presetColor.toLowerCase()
                  ? 'border-[var(--p-primary-color)] ring-2 ring-[var(--p-primary-color)]/40'
                  : 'border-[var(--p-surface-300)] dark:border-[var(--p-surface-600)]'
              "
              :style="{ backgroundColor: presetColor }"
              :title="presetColor"
              @click="emit('color', presetColor)"
            />
            <ColorControl :color="color" @color="emit('color', $event)" />
          </div>
          <label class="flex h-9 items-center gap-2 rounded-md px-2 text-xs">
            <Minus :size="16" />
            <input
              type="range"
              min="2"
              max="48"
              :value="brushWidth"
              class="w-24"
              :style="{ accentColor: color }"
              @input="emit('brush-width', Number(($event.target as HTMLInputElement).value))"
            />
          </label>
        </template>

        <template v-else-if="activePanel === 'censor'">
          <div class="flex h-9 items-center gap-1.5 px-1">
            <AppSelect
              :model-value="censorMode"
              :options="censorModes"
              :filter="false"
              :show-clear="false"
              option-label="name"
              option-value="value"
              input-class="!h-9 !rounded-md !text-xs w-28"
              @update:model-value="
                emit('censor-mode', String($event || 'blur') as 'blur' | 'pixelate')
              "
            />
            <label
              class="flex h-9 items-center gap-2 rounded-md px-2 text-xs text-[var(--p-surface-500)] dark:text-[var(--p-surface-200)]"
            >
              <span></span>
              <input
                type="range"
                min="0.05"
                max="0.8"
                step="0.05"
                :value="censorLevel"
                class="w-28"
                style="accent-color: var(--p-primary-color)"
                @input="emit('censor-level', Number(($event.target as HTMLInputElement).value))"
              />
            </label>
          </div>
        </template>

        <template v-else-if="activePanel === 'text'">
          <ColorControl :color="color" @color="emit('color', $event)" />
          <AppSelect
            :model-value="fontFamily"
            :options="fontOptions"
            :filter="false"
            :show-clear="false"
            option-label="name"
            option-value="value"
            container-class=""
            input-class="!h-9 !rounded-md !text-xs min-w-25 "
            @update:model-value="emit('font-family', String($event))"
          >
            <template #value="{ value }">
              <span class="text-xs" :style="{ fontFamily: String(value || fontFamily) }">
                {{ value || fontFamily }}
              </span>
            </template>
            <template #option="{ option }">
              <span class="text-sm" :style="{ fontFamily: option.value }">
                {{ option.name }}
              </span>
            </template>
          </AppSelect>

          <AppInputNumber
            :model-value="fontSize"
            :min="12"
            :max="96"
            :step="1"
            :use-grouping="false"
            container-class="w-24 min-w-24"
            input-class="!h-9 !w-full !rounded-md !text-xs"
            @update:model-value="emit('font-size', $event ?? fontSize)"
          >
            <template #icon>
              <CaseSensitive :size="16" />
            </template>
          </AppInputNumber>
          <div class="flex h-9 items-center rounded-md">
            <button
              v-for="align in textAlignOptions"
              :key="align.value"
              type="button"
              class="flex h-9 w-9 items-center cursor-pointer justify-center rounded-md text-[var(--p-surface-500)] transition hover:bg-[var(--p-primary-color)]/10 dark:text-[var(--p-surface-200)] dark:hover:bg-[var(--p-surface-600)]/60"
              :class="{
                '!bg-[var(--p-primary-color)]/90 dark:!bg-[var(--p-surface-600)] !text-white':
                  textAlign === align.value,
              }"
              :title="align.label"
              @click="emit('text-align', align.value)"
            >
              <component :is="align.icon" :size="16" />
            </button>
          </div>
        </template>

        <template v-else-if="activePanel === 'shapes'">
          <ColorControl :color="color" @color="emit('color', $event)" />
          <button
            v-for="shape in shapeTools"
            :key="shape.id"
            type="button"
            class="flex h-9 cursor-pointer items-center gap-2 rounded-md px-3 text-xs font-medium text-[var(--p-surface-500)] dark:text-[var(--p-surface-100)] transition hover:scale-110"
            @click="emit('tool', shape.id)"
          >
            <component :is="shape.icon" :size="16" />
            <!-- {{ shape.label }} -->
          </button>
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { defineComponent, h, ref, watch } from 'vue'
import {
  CaseSensitive,
  Circle,
  Crop,
  Eraser,
  Minus,
  Palette,
  Pencil,
  WandSparkles,
  Shapes,
  SlidersHorizontal,
  SmilePlus,
  Square,
  Type,
  MoveUpRight,
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  RotateCcw,
  RotateCw,
  Undo2,
} from 'lucide-vue-next'
import AppSelect from '@/components/atoms/selects/AppSelect.vue'
import AppInputNumber from '@/components/atoms/inputs/AppInputNumber.vue'
import EmojiPicker from '@/components/atoms/emoji/EmojiPicker.vue'

const props = defineProps<{
  activeTool: string
  color: string
  brushWidth: number
  fontSize: number
  fontFamily: string
  textAlign: string
  cropRatio: string
  censorMode: 'blur' | 'pixelate'
  censorLevel: number
  applyTick?: number
  openTextPanelTick?: number
}>()

const emit = defineEmits<{
  tool: [tool: string]
  filter: [filter: string]
  color: [color: string]
  'brush-width': [width: number]
  'font-size': [size: number]
  'font-family': [fontFamily: string]
  'text-align': [textAlign: string]
  'crop-ratio': [ratio: string]
  'rotate-left': []
  'rotate-right': []
  'crop-undo': []
  'censor-level': [level: number]
  'censor-mode': [mode: 'blur' | 'pixelate']
  emoji: [emoji: string]
}>()

const activePanel = ref('')
const selectedFilter = ref('')
const emojiPickerRef = ref<InstanceType<typeof EmojiPicker>>()

const primaryTools = [
  { id: 'crop', activeTool: 'crop', label: 'Recortar', icon: Crop },
  { id: 'filters', activeTool: '', label: 'Filtros', icon: WandSparkles },
  { id: 'draw', activeTool: 'draw', label: 'Pincel', icon: Pencil },
  { id: 'text', activeTool: 'text', label: 'Texto', icon: Type },
  { id: 'shapes', activeTool: '', label: 'Formas', icon: Shapes },
  { id: 'censor', activeTool: 'censor', label: 'Censurar seleccion', icon: Eraser },
  { id: 'emoji', activeTool: '', label: 'Emojis', icon: SmilePlus },
]

const shapeTools = [
  { id: 'rect', label: 'Rectangulo', icon: Square },
  { id: 'line', label: 'Linea', icon: Minus },
  { id: 'circle', label: 'Circulo', icon: Circle },
  { id: 'arrow', label: 'Flecha', icon: MoveUpRight },
]

const cropOptions = [
  { name: 'Libre', value: 'free' },
  { name: '1:1', value: '1:1' },
  { name: '4:3', value: '4:3' },
  { name: '3:4', value: '3:4' },
  { name: '16:9', value: '16:9' },
  { name: '9:16', value: '9:16' },
  { name: '3:2', value: '3:2' },
  { name: '2:3', value: '2:3' },
  { name: '21:9', value: '21:9' },
]

const filters = [
  { value: 'grayscale', label: 'Grises' },
  { value: 'sepia', label: 'Sepia' },
  { value: 'brightness', label: 'Brillo' },
  { value: 'contrast', label: 'Contraste' },
  { value: 'blur', label: 'Blur' },
  { value: 'clear', label: 'Limpiar' },
]

const fontFamilies = ['Arial', 'Inter', 'Verdana', 'Georgia', 'Courier New', 'Times New Roman']
const fontOptions = fontFamilies.map((font) => ({ name: font, value: font }))

const textAlignOptions = [
  { value: 'left', label: 'Alinear izquierda', icon: AlignLeft },
  { value: 'center', label: 'Centrar', icon: AlignCenter },
  { value: 'right', label: 'Alinear derecha', icon: AlignRight },
  { value: 'justify', label: 'Justificar', icon: AlignJustify },
]

const presetColors = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#22c55e',
  '#06b6d4',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#ffffff',
  '#111827',
]

const toolPanels = new Set(['crop', 'draw', 'text', 'censor'])
const censorModes = [
  { name: 'Blur', value: 'blur' },
  { name: 'Pixelar', value: 'pixelate' },
]

const selectPrimaryTool = (tool: string, event: MouseEvent) => {
  if (tool === 'emoji') {
    activePanel.value = ''
    const anchor = event.currentTarget as HTMLElement
    window.setTimeout(() => {
      emojiPickerRef.value?.togglePickerFromElement(anchor)
    })
    return
  }

  activePanel.value = activePanel.value === tool && tool !== 'text' ? '' : tool

  if (toolPanels.has(tool)) {
    emit('tool', tool)
  }
}

const selectFilter = (filter: string) => {
  selectedFilter.value = filter === 'clear' ? '' : filter
  emit('filter', filter)
}

const selectEmoji = (emoji: string) => {
  emit('emoji', emoji)
  activePanel.value = ''
}

watch(
  () => props.applyTick,
  () => {
    emojiPickerRef.value?.hidePicker()
    activePanel.value = ''
  },
)

watch(
  () => props.openTextPanelTick,
  () => {
    if (!props.openTextPanelTick) return
    emojiPickerRef.value?.hidePicker()
    activePanel.value = 'text'
  },
)

const ColorControl = defineComponent({
  props: {
    color: {
      type: String,
      required: true,
    },
  },
  emits: ['color'],
  setup(componentProps, { emit: componentEmit }) {
    return () =>
      h(
        'label',
        {
          class:
            'relative flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-[var(--p-surface-300)] text-[var(--p-surface-500)] transition hover:bg-[var(--p-primary-color)]/10 dark:border-[var(--p-surface-600)] dark:text-[var(--p-surface-200)]',
          title: 'Color personalizado',
        },
        [
          h(Palette, { size: 15 }),
          h('input', {
            type: 'color',
            class: 'absolute inset-0 h-full w-full cursor-pointer opacity-0',
            value: componentProps.color,
            onInput: (event: Event) =>
              componentEmit('color', (event.target as HTMLInputElement).value),
          }),
        ],
      )
  },
})
</script>

<style scoped>
.toolbar-panel-enter-active,
.toolbar-panel-leave-active {
  transition:
    opacity 140ms ease,
    transform 160ms ease;
}

.toolbar-panel-enter-from,
.toolbar-panel-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
